// Accessible "Move to" menu for a Kanban card — FE-042b.
//
// Drag-and-drop is the headline movement gesture, but it's gated on
// pointer + a viewport that can fit two columns side-by-side.
// Keyboard users and anyone on a narrow window get the same flow via
// this small kebab menu: open with Enter / click, choose a target
// status, fire the same ``onMove`` callback the drop handler uses.
//
// The menu renders through a portal anchored to ``document.body``.
// The board uses ``overflow-x-auto`` to scroll columns horizontally,
// and per CSS spec a non-``visible`` overflow on one axis forces
// browsers to clip the other axis as well. Without the portal, the
// last 1-2 menu items get cropped by the board's bottom edge when
// the card sits low in a tall column.

import { ChevronRight, MoreHorizontal } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  COMMITMENT_STATUSES_ORDERED,
  COMMITMENT_STATUS_COLUMN_META,
} from "./commitmentStatusMeta";
import type { CommitmentStatus } from "./types";


export interface CommitmentKanbanMoveToMenuProps {
  commitmentId: string;
  fromStatus: CommitmentStatus;
  /** Restricts the menu to the columns currently rendered on the
   * board. Defaults to the canonical 6-status order so the menu
   * still works when the caller doesn't filter (e.g. detail page in
   * a future iteration). */
  visibleStatuses?: CommitmentStatus[];
  onMove: (toStatus: CommitmentStatus) => void;
  disabled?: boolean;
}


interface PortalPosition {
  /** ``fixed``-positioned coordinates relative to the viewport so
   * the popover doesn't drift while the user scrolls inside the
   * board. */
  top: number;
  left: number;
  /** When the trigger sits near the viewport bottom we open
   * upward instead of downward so the last menu items stay
   * on-screen. */
  flipUp: boolean;
}


const MENU_WIDTH = 176; // 11rem = w-44
const MENU_GAP = 4;
const ESTIMATED_MENU_HEIGHT = 220;


export function CommitmentKanbanMoveToMenu({
  commitmentId, fromStatus, visibleStatuses, onMove, disabled,
}: CommitmentKanbanMoveToMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PortalPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Compute the portal anchor relative to the trigger every time the
  // menu opens. ``useLayoutEffect`` so the position lands before the
  // browser paints — flicker-free.
  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const flipUp =
      rect.bottom + ESTIMATED_MENU_HEIGHT + MENU_GAP > window.innerHeight
      && rect.top > ESTIMATED_MENU_HEIGHT + MENU_GAP;
    const top = flipUp
      ? rect.top - MENU_GAP // anchored from the menu's bottom — see below
      : rect.bottom + MENU_GAP;
    // Right-align the menu with the trigger, but clamp to the
    // viewport so a card near the right edge doesn't push it off.
    const desiredLeft = rect.right - MENU_WIDTH;
    const left = Math.max(8, Math.min(window.innerWidth - MENU_WIDTH - 8, desiredLeft));
    setPosition({ top, left, flipUp });
  }, [open]);

  // Click-outside dismiss + Escape close. Capture phase so the
  // ``mousedown`` lands before any portal-internal click handlers
  // re-render and detach the click target (same lesson as
  // FilterPopover — see its comment for the long version).
  useEffect(() => {
    if (!open) return;
    function handler(event: MouseEvent) {
      const button = buttonRef.current;
      const menu = menuRef.current;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (button && button.contains(target)) return;
      if (menu && menu.contains(target)) return;
      setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handler, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const targets = useMemo(() => {
    const allowed = new Set(
      visibleStatuses ?? COMMITMENT_STATUSES_ORDERED,
    );
    return COMMITMENT_STATUSES_ORDERED.filter(
      (s) => s !== fromStatus && allowed.has(s),
    );
  }, [visibleStatuses, fromStatus]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid={`commitments-card-move-button-${commitmentId}`}
        aria-label="Move Commitment"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="rounded p-1 text-text-tertiary hover:bg-recessed hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
      >
        <MoreHorizontal size={14} aria-hidden="true" />
      </button>
      {open && position && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid={`commitments-card-move-menu-${commitmentId}`}
          style={{
            position: "fixed",
            top: position.flipUp ? "auto" : position.top,
            bottom: position.flipUp
              ? window.innerHeight - position.top
              : "auto",
            left: position.left,
            width: MENU_WIDTH,
          }}
          className="z-[100] overflow-hidden rounded-md border border-border-subtle bg-raised text-xs shadow-lg"
        >
          <p className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
            Move to
          </p>
          <ul role="none" className="py-1">
            {targets.length === 0 ? (
              <li role="none">
                <p className="px-2 py-1 text-text-tertiary">
                  No other columns available.
                </p>
              </li>
            ) : (
              targets.map((status) => (
                <li key={status} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`commitments-card-move-option-${commitmentId}-${status}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpen(false);
                      onMove(status);
                    }}
                    className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-text-primary hover:bg-recessed"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={[
                          "inline-block h-1.5 w-1.5 rounded-full",
                          COMMITMENT_STATUS_COLUMN_META[status].accentDot,
                        ].join(" ")}
                      />
                      {COMMITMENT_STATUS_COLUMN_META[status].label}
                    </span>
                    <ChevronRight
                      size={12}
                      aria-hidden="true"
                      className="text-text-tertiary"
                    />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
